import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { FirestoreDateLike, toDateSafe, toMillisSafe } from '@utils/Date';

// ============================================================================
// Ledger de inventario (F5) — LECTURA desde el admin.
//
// Los movimientos los ESCRIBE el server (una sola copia de la aritmética del
// saldo); aquí solo se leen para el historial de la sucursal. Ese historial no
// existía: la sucursal solo mostraba un saldo, sin explicación de cómo llegó
// ahí — el CEDIS sí tenía su `distribution-center-inventory-history`.
//
// ⚠️ PARIDAD con `krispis-server/src/api/Inventory/InventoryMovement.ts`.
// ============================================================================

export enum InventoryMovementType {
    Sale = 'sale',
    SaleRevert = 'sale-revert',
    Reception = 'reception',
    Adjustment = 'adjustment',
    Count = 'count',
    /** Compra en la calle (plan de inventario F1): un gasto de insumo con cantidad. `purchaseOf` = el gasto. */
    Purchase = 'purchase',
    /** Merma (plan de inventario F2): siempre negativa, con motivo, PIN de quien autorizó y valuada. */
    Shrinkage = 'shrinkage'
}

export const INVENTORY_MOVEMENT_LABELS: Record<InventoryMovementType, string> = {
    [InventoryMovementType.Sale]: 'Venta',
    [InventoryMovementType.SaleRevert]: 'Devolución de venta',
    [InventoryMovementType.Reception]: 'Recepción',
    [InventoryMovementType.Adjustment]: 'Ajuste',
    [InventoryMovementType.Count]: 'Conteo físico',
    [InventoryMovementType.Purchase]: 'Compra en la calle',
    [InventoryMovementType.Shrinkage]: 'Merma'
};

/** Motivos de merma (plan de inventario §8.3, el negocio, 13-oct-2026). ⚠️ PARIDAD con el server. */
export const SHRINKAGE_REASONS = ['spilled', 'expired', 'spoiled', 'badPrep', 'broken', 'tasting', 'other'] as const;
export type ShrinkageReason = (typeof SHRINKAGE_REASONS)[number];

export const SHRINKAGE_REASON_LABELS: Record<ShrinkageReason, string> = {
    spilled: 'Se tiró / se regó',
    expired: 'Caducó',
    spoiled: 'Se echó a perder',
    badPrep: 'Mal preparado / se quemó',
    broken: 'Se rompió (envase, empaque)',
    tasting: 'Prueba o degustación',
    other: 'Otro'
};

export interface InventoryMovementItem {
    inputId: string;
    inputName: string;
    /** Unidades base. Negativo = salió, positivo = entró. En `count` es la DIFERENCIA. */
    quantity: number;
    unit: string;
    /** Solo en `count`: lo contado en absoluto (checkpoint del rebuild). */
    countedQuantity?: number;
    /** Solo en `count`: lo que el sistema creía tener. */
    expectedQuantity?: number;
    /** Solo en `count`: primer conteo de este insumo en la sucursal — punto de partida, no merma. */
    baseline?: boolean;
    /** Solo en `shrinkage`: $ por unidad base al momento. */
    unitCost?: number;
    /** Solo en `shrinkage`: |quantity| × unitCost, en pesos. */
    amount?: number;
}

export interface InventoryMovement {
    id?: string;
    branchOfficeId: string;
    type: InventoryMovementType;
    referenceId: string;
    orderId?: string;
    /** Solo en `purchase`: el id del gasto (`cash-flows`) que lo originó. */
    purchaseOf?: string;
    items: InventoryMovementItem[];
    date: Timestamp | Date;
    userId?: string;
    note?: string;
    /** Solo en `shrinkage`: el motivo. */
    reason?: ShrinkageReason;
    /** Solo en `shrinkage`: uid de quien autorizó con su PIN. */
    authorizedBy?: string;
    /** `{sucursal}_{yyyy-MM}` (México); lo sella el server. Los anteriores al 2026-10 no lo traen. */
    monthKey?: string;
}

/** Σ `amount` de una merma, en pesos (lo que el ER suma). */
export const getMovementAmount = (movement: InventoryMovement): number =>
    (movement.items || []).reduce((total, item) => total + (Number(item.amount) || 0), 0);

export const INVENTORY_MOVEMENTS_COLLECTION = 'inventory-movements';
export const inventoryMovementsRef = collection(firestore, INVENTORY_MOVEMENTS_COLLECTION);

/**
 * Fecha del movimiento, o `undefined` si el dato no sirve. ⚠️ Devuelve
 * `undefined` A PROPÓSITO en vez de un `Invalid Date`: quien la pinta pasa por
 * `format()`, que lanza `RangeError` con una fecha inválida y tumba la pantalla.
 */
export const getMovementDate = (movement: InventoryMovement): Date | undefined =>
    toDateSafe(movement.date as FirestoreDateLike);

/** Lo mismo para ORDENAR: `0` si no hay fecha, nunca `NaN` (que deja el sort indefinido). */
export const getMovementMillis = (movement: InventoryMovement): number =>
    toMillisSafe(movement.date as FirestoreDateLike);

/** Total de unidades que movió (suma de renglones) — para el resumen de la fila. */
export const getMovementTotalUnits = (movement: InventoryMovement): number =>
    (movement.items || []).reduce((total, item) => total + (Number(item.quantity) || 0), 0);

/**
 * Desde qué mes TODO movimiento trae `monthKey` (lo sella el server desde F2 del plan
 * de inventario). Un mes anterior se consulta por sucursal (lee todo su ledger) y se
 * recorta por fecha en cliente: es el único camino sin índice para lo viejo.
 */
export const MONTH_KEY_SINCE = '2026-10';

/** `{sucursal}_{yyyy-MM}`: paridad con `buildMonthKey` del server. */
export const buildMonthKey = (branchOfficeId: string, month: string): string => `${branchOfficeId}_${month}`;

/** `yyyy-MM` local de `date` desplazado `offset` meses. */
export const monthOf = (date: Date, offset = 0): string => {
    const shifted = new Date(date.getFullYear(), date.getMonth() + offset, 1);
    return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
};

export default {
    /**
     * Movimientos de una sucursal, del más reciente al más viejo.
     *
     * **Con `month`** (`yyyy-MM`, plan de inventario F4) la consulta va por `monthKey`
     * (un campo de igualdad: ~750 docs por mes con todo encendido); un mes anterior a
     * `MONTH_KEY_SINCE` cae a la consulta por sucursal y se recorta por fecha aquí.
     * **Sin `month`** lee el ledger entero de la sucursal (hoy ~200 docs; crece ~25 al
     * día): solo para el rebuild y usos puntuales, no para una pantalla.
     *
     * ⚠️ Combinar `where` con `orderBy('date')` exige un ÍNDICE COMPUESTO en Firestore
     * y sin él la pantalla truena entera ("failed-precondition"): el orden y el corte
     * se hacen en cliente, como en las cajas abiertas. El filtrado por insumo/tipo
     * también es en cliente: Firestore no consulta dentro de arrays de objetos.
     */
    async getMovements(
        branchOfficeId: string,
        options?: { max?: number; inputId?: string; type?: InventoryMovementType; month?: string }
    ): Promise<InventoryMovement[]> {
        const month = options?.month;
        const byMonthKey = !!month && month >= MONTH_KEY_SINCE;
        const snapshot = await getDocs(
            byMonthKey
                ? query(inventoryMovementsRef, where('monthKey', '==', buildMonthKey(branchOfficeId, month)))
                : query(inventoryMovementsRef, where('branchOfficeId', '==', branchOfficeId))
        );
        const inMonth = (movement: InventoryMovement): boolean => {
            if (!month || byMonthKey) return true;
            const date = getMovementDate(movement);
            return !!date && monthOf(date) === month;
        };
        let movements = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }) as InventoryMovement)
            .filter(inMonth)
            .sort((a, b) => getMovementMillis(b) - getMovementMillis(a))
            .slice(0, options?.max || 300);
        if (options?.type) {
            movements = movements.filter((movement) => movement.type === options.type);
        }
        if (options?.inputId) {
            movements = movements.filter((movement) =>
                (movement.items || []).some((item) => item.inputId === options.inputId)
            );
        }
        return movements;
    }
};
